import { ChevronRight } from "lucide-react";
import { Private, usePrivacy } from "../../components/Privacy";
import type { DashboardData, OwnerFilter } from "../../types";
import type { ReportingActivity } from "../../types/reporting";
import { insightBudget } from "./insight-budget";
import { figure, tickerClass } from "./insight-format";

export default function InsightBudget({ data, owner, month, today, spending, activity, onOpenBudgets, onOpenSpending }: {
  data: DashboardData; owner: OwnerFilter; month: string; today: string; spending: number | null;
  activity: ReportingActivity; onOpenBudgets: () => void;
  onOpenSpending?: () => void;
}) {
  const { hidden } = usePrivacy();
  const budget = insightBudget(data, owner, month, today, spending);
  const currency = data.baseCurrency;
  const over = budget.remaining !== null && budget.remaining < 0;
  const used = budget.limit && spending !== null ? Math.min(100, (spending / budget.limit) * 100) : 0;
  const spent = <strong><Private>{figure(spending, currency)}</Private></strong>;

  return (
    <section className="panel insight-panel insight-budget">
      <div className="section-heading">
        <div><h2>Budget</h2><p>{owner === "All" ? "Household" : owner} · Full month, all activity · {currency}</p></div>
        <button type="button" className="insight-text-button" onClick={onOpenBudgets}>Manage</button>
      </div>
      {activity !== "all" && <p className="insight-note">Budget figures include both everyday and trip spending.</p>}
      <div className="insight-budget-line">
        <span className="insight-budget-spent">
          {onOpenSpending && spending !== null ? (
            <button type="button" className="insight-budget-link" onClick={onOpenSpending} aria-label="View full month spending">
              {spent}<ChevronRight size={13} aria-hidden="true" />
            </button>
          ) : spent}
          {budget.limit !== null && <small>of <Private>{figure(budget.limit, currency)}</Private></small>}
        </span>
        {budget.limit !== null && budget.remaining !== null && (
          <span className={`insight-ticker ${over ? "up" : "down"}`}>
            <Private>{figure(Math.abs(budget.remaining), currency)}</Private> {over ? "over" : "left"}
          </span>
        )}
      </div>
      {!hidden && budget.limit !== null && budget.limit > 0 && spending !== null && (
        <div className="insight-meter" role="img" aria-label={`${Math.round(used)}% of monthly budget used`}>
          <i className={over ? "over" : ""} style={{ width: `${used}%` }} />
        </div>
      )}
      {budget.current && budget.projected !== null && (
        <div className="insight-kv">
          <span>Month-end estimate</span>
          <strong className={`insight-ticker ${budget.projectedRemaining === null ? "flat" : tickerClass(-budget.projectedRemaining)}`}>
            <Private>{figure(budget.projected, currency)}</Private>
          </strong>
        </div>
      )}
      {budget.limit === null && <p className="insight-note">{budget.budget?.amount != null ? "This budget uses a different currency; a comparable limit is unavailable." : "No overall budget for this month. Add one in Budgets to track what’s left."}</p>}
      {budget.current && (
        <p className="insight-note">
          {budget.projected === null ? "An estimate needs at least seven elapsed days and complete exchange rates." : (
            <>Assumes the daily average so far continues; upcoming bills aren’t included.{budget.projectedRemaining !== null && <Private> At this pace you’d finish {figure(Math.abs(budget.projectedRemaining), currency)} {budget.projectedRemaining < 0 ? "over" : "under"}.</Private>}</>
          )}
        </p>
      )}
    </section>
  );
}
